// Display-only readers for payment facts already stored on a transaction.
// Nothing here calculates a charge; every value is read back or omitted.
import { isProviderFundingConfirmed, hasAuthoritativeReleaseEvidence, hasTrustTradeDirectAcceptRelease } from './transactionState';

// Paid facts may be shown once funding OR a later authoritative release is
// recorded (a released deal whose tradesafe_state is outside the funded list
// must still show how/when it was paid).
export function hasPaymentEvidence(transaction = {}) {
  const t = transaction || {};
  return isProviderFundingConfirmed(t) || hasAuthoritativeReleaseEvidence(t) || hasTrustTradeDirectAcceptRelease(t);
}

const METHOD_LABELS = { CARD: 'Card', OZOW: 'Ozow Instant EFT', EFT: 'EFT', INSTANT_EFT: 'Instant EFT' };

export function paymentMethodLabel(transaction = {}) {
  const raw = String(transaction.provider_payment_method || transaction.tradesafe_payment_method || transaction.payment_method || '').trim().toUpperCase();
  if (!raw) return null;
  return METHOD_LABELS[raw] || raw.replace(/_/g, ' ');
}

export function formatSast(value) {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  const text = date.toLocaleString('en-ZA', { timeZone: 'Africa/Johannesburg', day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false });
  return `${text} SAST`;
}

const cents = (value) => Math.round(Number(value) * 100);

// The fee/VAT split of the quote the buyer accepted, shown only when it
// reconciles exactly with the provider-confirmed figures. Otherwise null.
export function providerFeeSplit(transaction = {}, confirmedProviderFee, confirmedTotal) {
  const quote = transaction.accepted_checkout_quote;
  if (!quote || confirmedProviderFee == null || confirmedTotal == null) return null;
  const fee = Number(quote.buyer_tradesafe_fee_cents);
  const vat = Number(quote.buyer_tradesafe_vat_cents);
  if (!Number.isInteger(fee) || !Number.isInteger(vat) || vat < 0 || vat > fee) return null;
  if (fee !== cents(confirmedProviderFee)) return null;
  if (Number(quote.buyer_pays_cents) !== cents(confirmedTotal)) return null;
  const providerMethod = String(transaction.provider_payment_method || '').toUpperCase();
  if (providerMethod && quote.payment_method && providerMethod !== String(quote.payment_method).toUpperCase()) return null;
  return { feeExclVat: (fee - vat) / 100, vat: vat / 100, total: fee / 100 };
}

export function paymentRecord(transaction = {}) {
  if (!hasPaymentEvidence(transaction)) return null;
  const record = {
    method: paymentMethodLabel(transaction),
    paidAt: formatSast(transaction.funds_received_at),
    reference: transaction.tradesafe_id || null,
  };
  return record.method || record.paidAt || record.reference ? record : null;
}

// Journey step index -> stored timestamp. Only real recorded times; no estimates.
export function journeyStepTimes(transaction = {}) {
  return {
    2: formatSast(transaction.funds_received_at),
    4: formatSast(transaction.buyer_confirmed_receipt_at || transaction.delivery_confirmed_at),
    5: formatSast(transaction.funds_released_at || transaction.released_at),
    6: formatSast(transaction.withdrawal_requested_at || transaction.payout_processing_started_at),
    7: formatSast(transaction.withdrawal_completed_at),
  };
}
